import React, { useMemo, useState } from 'react';
import { canCurrentUser } from '@utils/Permissions';
import { Capability } from '@utils/Permissions/Capabilities';
import { Chip, Stack, Typography, Tooltip } from '@mui/material';
import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { format } from 'date-fns';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { DeliveryType, Order, orderStatusTranslations, paymentMethodsTranslations } from '@api/Orders/Order';
import { currencyFormatter } from '@utils/Numbers';
import IconButton from '@mui/material/IconButton';
import { Delete, Visibility } from '@mui/icons-material';
import useConfirmationModal from '@hooks/useConfirmationModal';
import useOpenModal from '@hooks/useOpenModal';
import OrdersService from '@api/Orders/OrderCtrl';
import { ModalConfirmation } from '@components/Utilities/Modals/ModalConfirmation';
import { showAlertMessage } from '@utils/Alerts/Message';
import { ModalViewOrder } from '@components/Modals/ModalViewOrder';
import { BranchOffice } from '@api/BranchOffices/BranchOffice';
import { PaymentMethod, getOrderPaidBy } from '@api/Orders/OrderPayments';

interface DashboardReportsScreen {
    orders: Order[];
    branchOffices: BranchOffice[];
    /**
     * Recargar la lista. **El tipo dice `Promise<void>` porque el padre es
     * `async` y aquí SE ESPERA**: con `() => void` el `await` de abajo mentía
     * (el linter lo cazó) y el mensaje de "órdenes eliminadas" prometía una
     * lista que podía no estar refrescada todavía.
     */
    getOrdersData: () => void | Promise<void>;
}

const OrdersTable: React.FunctionComponent<DashboardReportsScreen> = (props) => {
    const { orders, getOrdersData, branchOffices } = props;
    const ordersService = useMemo(() => new OrdersService(), []);
    const [currentOrder, setCurrentOrder] = useState<Order | undefined>(undefined);
    const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel | undefined>(undefined);
    const modalViewOrder = useOpenModal();
    const modalDeleteMultiple = useConfirmationModal();

    const handleViewOrder = (order: Order) => {
        setCurrentOrder(order);
        modalViewOrder.toggle();
    };

    const getBranchOfficeName = (branchOfficeId: string): string => {
        const branchOffice = branchOffices.find((office) => office.id === branchOfficeId);
        return branchOffice?.name || 'N/A';
    };

    const getPaymentMethods = (order: Order): string[] => {
        const methods: string[] = [];

        if (order.stripe) {
            methods.push('Stripe');
        }
        if (getOrderPaidBy(order, PaymentMethod.Cash) > 0) {
            methods.push('CashMXN');
        }
        if (getOrderPaidBy(order, PaymentMethod.CreditCard) > 0 && !order.stripe) {
            methods.push('CreditCard');
        }
        if (getOrderPaidBy(order, PaymentMethod.BankTransfer) > 0) {
            methods.push('BankTransfer');
        }

        return methods;
    };

    const formatPaymentMethods = (order: Order): string => {
        const methods = getPaymentMethods(order);

        if (methods.length === 0) {
            return 'N/A';
        }

        if (methods.length > 1) {
            return 'Múltiples métodos';
        }

        const singleMethod = methods[0];
        return paymentMethodsTranslations[singleMethod] || singleMethod;
    };

    const getPaymentMethodsTooltip = (order: Order): string => {
        const methods = getPaymentMethods(order);

        if (methods.length === 0) {
            return 'No se especificó método de pago';
        }

        if (methods.length === 1) {
            return paymentMethodsTranslations[methods[0]] || methods[0];
        }

        return methods.map((method) => paymentMethodsTranslations[method] || method).join(', ');
    };

    // Eliminar pedidos (deshace puntos y caja): los dos admins; el Dashboard del admin de
    // sucursal ya viene acotado a lo suyo. El gate de verdad, pedido por pedido y por
    // sucursal, está en `purgeOrder`.
    const canPurge = canCurrentUser(Capability.OrdersPurge);

    const onDeleteMultipleOrders = async () => {
        showLoader();
        try {
            if (!selectedIds || selectedIds.length === 0) {
                showAlertError('No hay órdenes seleccionadas para eliminar');
                return;
            }

            const idsToDelete = selectedIds.map((id) => id.toString());
            // purge (no delete crudo): deshace también caja/mesa/puntos de cada pedido
            await ordersService.purgeMultipleOrders(idsToDelete);
            setSelectedIds(undefined);
            await getOrdersData();
            showAlertMessage(`${idsToDelete.length} órdenes eliminadas correctamente`);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onConfirmDeleteMultipleOrders = () => {
        if (!selectedIds || selectedIds.length === 0) {
            showAlertError('No hay órdenes seleccionadas para eliminar');
            return;
        }

        modalDeleteMultiple.setModalData(
            'Eliminar órdenes',
            `Vas a eliminar ${selectedIds.length} pedido(s). Esta acción NO se puede deshacer.\n\n` +
                'De cada uno: se borra con su historial de actividad, su efectivo sale del corte de caja, ' +
                'los puntos del cliente se recalculan (pierde los de ese pedido) y su mesa se libera si ya no tiene pedidos activos.'
        );
    };

    const columns: GridColDef<Order>[] = [
        {
            field: 'ID',
            flex: 2,
            renderHeader: () => (
                <Typography color="text.secondary" fontWeight={600}>
                    ID
                </Typography>
            ),
            renderCell: ({ row }) => <Typography>{row.shortId}</Typography>
        },
        {
            field: 'Fecha',
            flex: 2,
            renderHeader: () => (
                <Typography color="text.secondary" fontWeight={600}>
                    Fecha
                </Typography>
            ),
            renderCell: ({ row }) => <Typography>{format(row.date.toDate(), 'dd/MM/yyyy HH:mm')}</Typography>
        },
        {
            field: 'Cliente',
            flex: 2,
            renderHeader: () => (
                <Typography color="text.secondary" fontWeight={600}>
                    Cliente
                </Typography>
            ),
            renderCell: ({ row }) => <Typography>{row.customer?.profile?.name}</Typography>
        },
        {
            field: 'Sucursal',
            flex: 3,
            renderHeader: () => (
                <Typography color="text.secondary" fontWeight={600}>
                    Sucursal
                </Typography>
            ),
            renderCell: ({ row }) => <Typography>{getBranchOfficeName(row.branchOfficeId)}</Typography>
        },
        {
            field: 'Entrega',
            flex: 2,
            renderHeader: () => (
                <Typography color="text.secondary" fontWeight={600}>
                    Entrega
                </Typography>
            ),
            renderCell: ({ row }) => {
                const label =
                    row.deliveryType === DeliveryType.Delivery
                        ? 'Delivery'
                        : row.deliveryType === DeliveryType.Pickup
                        ? 'Pickup'
                        : 'Mostrador';
                return (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography>{label}</Typography>
                        {row.isReservation && <Chip label="Reserva" color="success" size="small" />}
                    </Stack>
                );
            }
        },
        {
            field: 'Programada',
            flex: 2,
            renderHeader: () => (
                <Typography color="text.secondary" fontWeight={600}>
                    Programada
                </Typography>
            ),
            renderCell: ({ row }) => {
                const scheduled = row.isReservation
                    ? row.reservationDate
                    : row.deliveryType === DeliveryType.Pickup
                    ? row.pickupDate
                    : undefined;
                return <Typography>{scheduled ? format(scheduled.toDate(), 'dd/MM/yyyy HH:mm') : '—'}</Typography>;
            }
        },
        {
            field: 'Estatus',
            flex: 2,
            renderHeader: () => (
                <Typography color="text.secondary" fontWeight={600}>
                    Estatus
                </Typography>
            ),
            renderCell: ({ row }) => <Typography>{orderStatusTranslations[row.status] || 'N/A'}</Typography>
        },
        {
            field: 'paymentMethod',
            flex: 2,
            renderHeader: () => (
                <Typography color="text.secondary" fontWeight={600}>
                    Método de pago
                </Typography>
            ),
            renderCell: (params: any) => {
                const order = params.row as Order;
                const displayText = formatPaymentMethods(order);
                const tooltipText = getPaymentMethodsTooltip(order);

                return (
                    <Tooltip title={tooltipText} arrow>
                        <Typography
                            sx={{
                                cursor: tooltipText !== displayText ? 'pointer' : 'default',
                                textDecoration: tooltipText !== displayText ? 'underline dotted' : 'none'
                            }}
                        >
                            {displayText}
                        </Typography>
                    </Tooltip>
                );
            }
        },
        {
            field: 'Total',
            flex: 1,
            renderHeader: () => (
                <Typography color="text.secondary" fontWeight={600}>
                    Total
                </Typography>
            ),
            renderCell: ({ row }) => <Typography>{currencyFormatter(row.total)}</Typography>
        },
        {
            field: 'Actions',
            disableExport: true,
            sortable: false,
            flex: 1.5,
            renderHeader: () => (
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography color="text.secondary" fontWeight={600}>
                        Acciones
                    </Typography>
                    {canPurge && selectedIds && selectedIds.length > 0 && (
                        <IconButton onClick={onConfirmDeleteMultipleOrders} color="error" size="small">
                            <Delete />
                        </IconButton>
                    )}
                </Stack>
            ),
            renderCell: (params) => (
                <Stack direction="row" spacing={1}>
                    <IconButton onClick={() => handleViewOrder(params.row)}>
                        <Visibility />
                    </IconButton>
                </Stack>
            )
        }
    ];

    return (
        <Stack spacing={2} height="100%">
            <DataGrid
                sx={{
                    border: 0,
                    flex: 1,
                    minHeight: 0,
                    '& .MuiDataGrid-columnHeaders': { backgroundColor: '#F4F6F8' }
                }}
                rows={orders}
                columns={columns}
                getRowId={(row) => row.id || row.shortId}
                pageSizeOptions={[10, 25, 50]}
                disableColumnSelector
                disableColumnFilter
                checkboxSelection={canPurge}
                onRowSelectionModelChange={(newSelection: GridRowSelectionModel) => {
                    setSelectedIds(newSelection);
                }}
                rowSelectionModel={selectedIds}
                initialState={{
                    pagination: {
                        paginationModel: { page: 0, pageSize: 10 }
                    }
                }}
            />
            <ModalConfirmation modalConfirmation={modalDeleteMultiple} onConfirmation={onDeleteMultipleOrders} />
            {currentOrder && (
                <ModalViewOrder
                    modalViewOrder={modalViewOrder}
                    order={currentOrder}
                    branchOffices={branchOffices}
                    onDeleted={getOrdersData}
                ></ModalViewOrder>
            )}
        </Stack>
    );
};

export default OrdersTable;
